import type { Schemas } from "@artisan/api-client";
import { Badge, Button, Card, Field, Input, Textarea } from "@artisan/ui";
import { Briefcase, Crosshair, Link2, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";

import { Screen } from "../components/screen";
import { api, unwrap } from "../lib/api";
import { projectTitle } from "../project/actions";
import { type Project } from "../project/model";
import { updateProject, useProject } from "../project/store";

const PHONE = /^\+?[0-9 ()-]{6,24}$/;

/** A text field that saves to the project when it loses focus. */
function ProjectField({
  project,
  label,
  get,
  set,
  hint,
  type,
  error,
}: {
  project: Project;
  label: string;
  get: (p: Project) => string;
  set: (p: Project, v: string) => void;
  hint?: string;
  type?: string;
  error?: (v: string) => string | null;
}) {
  const [value, setValue] = useState(get(project));
  const problem = error?.(value) ?? null;
  return (
    <Field
      label={label}
      value={value}
      type={type}
      hint={hint}
      error={problem ?? undefined}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value !== get(project)) void updateProject(project.id, (p) => set(p, value));
      }}
    />
  );
}

function DealPicker({ project }: { project: Project }) {
  const [q, setQ] = useState("");
  const [deals, setDeals] = useState<Schemas["DealOut"][] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      unwrap(api().GET("/v1/crm/deals", { params: { query: { status: "open", limit: 8, ...(q.trim() ? { q: q.trim() } : {}) } } }))
        .then((page) => live && (setDeals(page.items), setFailed(false)))
        .catch(() => live && setFailed(true));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q]);

  if (project.deal_id) {
    return (
      <div className="flex items-center gap-3 rounded-lg border bg-primary-soft/50 p-3">
        <Briefcase className="size-4 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{project.deal_title ?? "CRM захиалга"}</p>
          <p className="text-xs text-muted-foreground">Хэмжилт энэ захиалгын түүхэнд бүртгэгдэнэ.</p>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Захиалгаас салгах"
          onClick={() =>
            void updateProject(project.id, (p) => {
              p.deal_id = null;
              p.deal_title = null;
            })
          }
        >
          <X aria-hidden />
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Захиалгын нэр, дугаар, утас" className="h-11 pl-9" aria-label="CRM захиалга хайх" />
      </div>
      {failed ? (
        <p className="text-sm text-muted-foreground">Офлайн үед захиалга холбох боломжгүй. Дараа нь холбож болно.</p>
      ) : (
        <ul className="max-h-60 divide-y overflow-y-auto rounded-lg border">
          {(deals ?? []).map((d) => (
            <li key={d.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-accent"
                onClick={() =>
                  void updateProject(project.id, (p) => {
                    p.deal_id = d.id;
                    p.deal_title = d.title;
                    if (!p.title) p.title = d.title;
                    if (!p.site.address && d.site_address) p.site.address = d.site_address;
                    if (!p.site.customer_name && d.contact) p.site.customer_name = d.contact.name;
                    if (!p.site.customer_phone && d.contact?.phone) p.site.customer_phone = d.contact.phone;
                  })
                }
              >
                <Link2 className="size-4 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{d.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    #{d.number} · {d.contact?.name ?? "Харилцагчгүй"}
                  </span>
                </span>
              </button>
            </li>
          ))}
          {deals && deals.length === 0 ? <li className="px-3 py-3 text-sm text-muted-foreground">Олдсонгүй</li> : null}
        </ul>
      )}
    </div>
  );
}

export function SiteScreen() {
  const { id } = useParams();
  const project = useProject(id);
  const navigate = useNavigate();
  const [locating, setLocating] = useState(false);
  if (!project) return <Navigate to="/" replace />;
  const current = project;

  function locate() {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        void updateProject(current.id, (p) => {
          p.site.lat = Math.round(pos.coords.latitude * 1e6) / 1e6;
          p.site.lng = Math.round(pos.coords.longitude * 1e6) / 1e6;
        });
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  }

  return (
    <Screen
      back={`/p/${project.id}`}
      title="Захиалагч ба объект"
      subtitle={projectTitle(project)}
      actions={
        <Button size="lg" onClick={() => navigate(`/p/${project.id}`)}>
          Хадгалаад үргэлжлүүлэх
        </Button>
      }
    >
      <div className="grid grid-cols-[1.3fr_1fr] gap-5">
        <Card className="space-y-4 p-6">
          <ProjectField project={project} label="Төслийн нэр" get={(p) => p.title} set={(p, v) => (p.title = v)} hint="Жишээ нь: Номин · Гал тогоо" />
          <div className="grid grid-cols-2 gap-4">
            <ProjectField project={project} label="Харилцагчийн нэр" get={(p) => p.site.customer_name} set={(p, v) => (p.site.customer_name = v)} />
            <ProjectField
              project={project}
              label="Утас"
              type="tel"
              get={(p) => p.site.customer_phone}
              set={(p, v) => (p.site.customer_phone = v)}
              error={(v) => (v.trim() && !PHONE.test(v.trim()) ? "Утасны дугаар буруу байна" : null)}
            />
          </div>
          <ProjectField project={project} label="Объектын хаяг" get={(p) => p.site.address} set={(p, v) => (p.site.address = v)} hint="Дүүрэг, хороо, байр, тоот" />
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={Crosshair} onClick={locate} disabled={locating}>
              {locating ? "Байршил тогтоож байна…" : "Одоогийн байршлыг авах"}
            </Button>
            {project.site.lat !== undefined ? (
              <Badge variant="success">
                {project.site.lat.toFixed(5)}, {project.site.lng?.toFixed(5)}
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground">Ойрын нийлүүлэгч хайхад хэрэглэнэ.</span>
            )}
          </div>
          <ProjectField project={project} label="Хэмжилт хийсэн" get={(p) => p.surveyor_name} set={(p, v) => (p.surveyor_name = v)} />
        </Card>
        <div className="space-y-5">
          <Card className="space-y-3 p-6">
            <h2 className="font-semibold">CRM захиалга</h2>
            <DealPicker project={project} />
          </Card>
          <Card className="space-y-2 p-6">
            <label htmlFor="general-note" className="font-semibold">
              Ерөнхий тэмдэглэл
            </label>
            <Textarea
              id="general-note"
              rows={5}
              defaultValue={project.notes.find((n) => n.id === "N0")?.text ?? ""}
              placeholder="Харилцагчийн хүсэлт, анхаарах зүйл…"
              onBlur={(e) => {
                const text = e.target.value.trim();
                void updateProject(project.id, (p) => {
                  p.notes = p.notes.filter((n) => n.id !== "N0");
                  if (text) p.notes.unshift({ id: "N0", text });
                });
              }}
            />
          </Card>
        </div>
      </div>
    </Screen>
  );
}
